import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { IMAGES } from '@/assets/images';
import { motion } from 'framer-motion';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { Calendar, Clock, Users, TrendingUp, Smartphone, Zap, Shield, BarChart3, CheckCircle2, ArrowRight } from 'lucide-react';

export default function Home() {
  const features = [
    {
      icon: Calendar,
      title: 'Smart Scheduling',
      description: 'AI-powered appointment management that learns your business patterns'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Design',
      description: 'Seamless booking experience across all devices and platforms'
    },
    {
      icon: Users,
      title: 'Client Management',
      description: 'Comprehensive customer profiles with booking history and preferences'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Real-time insights into booking trends and business performance'
    },
    {
      icon: Zap,
      title: 'Automated Reminders',
      description: 'Reduce no-shows with intelligent SMS and email notifications'
    },
    {
      icon: Shield,
      title: 'Secure Payments',
      description: 'Contactless payment processing with industry-leading security'
    }
  ];

  const benefits = [
    'Reduce administrative time by up to 70%',
    'Increase booking conversion rates by 40%',
    'Decrease no-show rates by 60%',
    'Improve customer satisfaction scores',
    'Scale operations without additional staff',
    'Access business insights 24/7 from anywhere'
  ];

  return (
    <Layout>
      <div className="relative">
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="hero-background">
            <img 
              src={IMAGES.HERO_BG_10} 
              alt="Business scheduling" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="hero-overlay" />
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div 
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
            >
              <motion.h1 
                className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ ...springPresets.gentle, delay: 0.1 }}
              >
                Transform Your Small Business with Smart Booking Software
              </motion.h1>
              <motion.p 
                className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ...springPresets.gentle, delay: 0.2 }}
              >
                Discover how modern scheduling solutions are revolutionizing small business operations in 2026
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: 0.3 }}
              >
                <Button 
                  size="lg" 
                  className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                  asChild
                >
                  <a href="#article">Read the Complete Guide</a>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div key={index} variants={staggerItem}>
                    <Card className="p-6 h-full hover:shadow-lg transition-shadow">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        <article id="article" className="py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={springPresets.gentle}
              >
                <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center">
                  The Complete Guide to Booking Software for Small Business in 2026
                </h2>
                
                <div className="prose prose-lg max-w-none">
                  <div className="mb-12">
                    <img 
                      src={IMAGES.ARTICLE_IMG_3} 
                      alt="Modern booking interface" 
                      className="w-full rounded-2xl shadow-xl mb-6"
                    />
                  </div>

                  <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                    In today's fast-paced digital economy, small businesses face unprecedented challenges in managing appointments, customer relationships, and operational efficiency. Booking software has emerged as a critical tool that can make or break a small business's success. This comprehensive guide explores everything you need to know about selecting and implementing the right booking solution for your business in 2026.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Understanding the Modern Booking Software Landscape</h3>
                  
                  <p className="mb-6 leading-relaxed">
                    The booking software market has evolved dramatically over the past few years. What once consisted of simple calendar tools has transformed into sophisticated platforms that integrate artificial intelligence, predictive analytics, and omnichannel customer engagement. Modern booking systems are no longer just about scheduling appointments—they're comprehensive business management solutions that handle everything from customer relationship management to payment processing and business intelligence.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Small businesses in 2026 have access to tools that were once exclusive to enterprise-level organizations. Cloud-based solutions have democratized access to powerful features like automated marketing, real-time analytics, and mobile-first interfaces. The key is understanding which features align with your specific business needs and growth trajectory.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Why Small Businesses Need Dedicated Booking Software</h3>

                  <p className="mb-6 leading-relaxed">
                    The traditional methods of managing appointments—phone calls, paper calendars, and spreadsheets—are no longer viable in today's competitive marketplace. Customers expect instant booking capabilities, 24/7 availability, and seamless digital experiences. Businesses that fail to meet these expectations risk losing customers to more technologically advanced competitors.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Booking software addresses multiple pain points simultaneously. It eliminates double-bookings and scheduling conflicts through real-time calendar synchronization. It reduces no-shows by sending automated reminders via SMS and email. It captures valuable customer data that can be used for personalized marketing and service improvements. Most importantly, it frees up staff time that would otherwise be spent on administrative tasks, allowing them to focus on delivering exceptional customer service.
                  </p>

                  <div className="my-12 p-8 bg-accent/10 rounded-2xl border border-accent/20">
                    <h4 className="text-2xl font-semibold mb-4 text-accent">Key Statistics</h4>
                    <ul className="space-y-3">
                      {benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Essential Features to Look For</h3>

                  <p className="mb-6 leading-relaxed">
                    When evaluating booking software, certain features have become non-negotiable in 2026. Mobile-first design is paramount—over 70% of bookings now originate from smartphones and tablets. Your chosen platform must deliver a flawless mobile experience for both customers and staff. This includes responsive design, fast loading times, and intuitive touch interfaces.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    AI-powered scheduling represents another critical capability. Modern systems use machine learning algorithms to optimize appointment slots, predict busy periods, and suggest optimal staffing levels. These intelligent features help businesses maximize revenue while minimizing idle time and resource waste.
                  </p>

                  <div className="mb-12">
                    <img 
                      src={IMAGES.BUSINESS_OWNER_5} 
                      alt="Business owner using booking software" 
                      className="w-full rounded-2xl shadow-xl mb-6"
                    />
                  </div>

                  <p className="mb-6 leading-relaxed">
                    Integration capabilities cannot be overlooked. Your booking software should seamlessly connect with your existing tools—payment processors, email marketing platforms, accounting software, and customer relationship management systems. This integration eliminates data silos and creates a unified view of your business operations.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">The Role of Automation in Modern Booking Systems</h3>

                  <p className="mb-6 leading-relaxed">
                    Automation has become the cornerstone of efficient booking management. Automated reminder systems have proven particularly valuable, reducing no-show rates by up to 60% according to recent industry studies. These systems send personalized reminders at optimal intervals—typically 24 hours and 2 hours before appointments—using the customer's preferred communication channel.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Beyond reminders, automation extends to waitlist management, follow-up communications, and review requests. When a cancellation occurs, automated systems can instantly notify waitlisted customers, filling the slot without manual intervention. Post-appointment follow-ups can be triggered automatically, requesting feedback and encouraging repeat bookings.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Payment Processing and Financial Management</h3>

                  <p className="mb-6 leading-relaxed">
                    Contactless payment processing has transitioned from a nice-to-have feature to an essential requirement. Modern booking software integrates secure payment gateways that support multiple payment methods—credit cards, digital wallets, and even cryptocurrency in some cases. This flexibility improves conversion rates and reduces payment friction.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Advanced systems offer deposit collection, installment plans, and dynamic pricing capabilities. These features help businesses manage cash flow more effectively while providing customers with flexible payment options. Real-time financial reporting gives business owners instant visibility into revenue streams, outstanding payments, and financial trends.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Customer Experience and Engagement</h3>

                  <p className="mb-6 leading-relaxed">
                    The booking experience itself has become a critical touchpoint in the customer journey. Leading platforms offer customizable booking pages that reflect your brand identity, creating a cohesive experience from discovery to appointment completion. Customers can view real-time availability, select their preferred service provider, and complete bookings in under a minute.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Social media integration has emerged as a powerful feature in 2026. Customers can now book appointments directly through Facebook, Instagram, and other social platforms without leaving their preferred apps. This omnichannel approach meets customers where they already spend their time, reducing booking friction and increasing conversion rates.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Analytics and Business Intelligence</h3>

                  <p className="mb-6 leading-relaxed">
                    Data-driven decision making has become essential for small business success. Modern booking software provides comprehensive analytics dashboards that track key performance indicators—booking rates, customer retention, revenue per appointment, and staff utilization. These insights enable businesses to identify trends, optimize operations, and make informed strategic decisions.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Predictive analytics takes this further by forecasting future demand based on historical patterns, seasonal trends, and external factors. Businesses can use these predictions to adjust staffing levels, plan marketing campaigns, and manage inventory more effectively. The ability to anticipate customer needs and market conditions provides a significant competitive advantage.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Security and Compliance Considerations</h3>

                  <p className="mb-6 leading-relaxed">
                    Data security has never been more critical. Booking software handles sensitive customer information—personal details, payment data, and health information in some industries. Leading platforms implement bank-level encryption, regular security audits, and compliance with regulations like GDPR and CCPA. When evaluating solutions, verify their security certifications and data protection policies.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Backup and disaster recovery capabilities ensure business continuity even in worst-case scenarios. Cloud-based systems automatically backup data and provide redundancy across multiple servers. This protection is invaluable for small businesses that may lack dedicated IT resources.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Scalability and Growth Support</h3>

                  <p className="mb-6 leading-relaxed">
                    As your business grows, your booking software should grow with you. Scalable platforms support multiple locations, unlimited staff members, and expanding service offerings without requiring system changes or migrations. This flexibility protects your initial investment and ensures long-term viability.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Multi-location management features enable businesses to expand geographically while maintaining centralized control. Owners can monitor performance across all locations, standardize processes, and share resources efficiently. This capability is particularly valuable for franchise operations and businesses planning regional expansion.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Implementation and Training</h3>

                  <p className="mb-6 leading-relaxed">
                    Successful software adoption depends on proper implementation and staff training. The best platforms offer comprehensive onboarding programs, including video tutorials, live training sessions, and dedicated support teams. Look for solutions with intuitive interfaces that minimize the learning curve and accelerate time-to-value.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Migration support is crucial if you're switching from another system. Quality providers offer data migration services, ensuring customer information, appointment history, and other critical data transfer seamlessly to the new platform. This support minimizes disruption and maintains business continuity during the transition.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Cost Considerations and ROI</h3>

                  <p className="mb-6 leading-relaxed">
                    Booking software pricing models vary widely, from free basic plans to enterprise solutions costing hundreds of dollars monthly. When evaluating costs, consider the total cost of ownership—subscription fees, transaction charges, add-on features, and implementation costs. More importantly, calculate the expected return on investment based on time savings, increased bookings, and reduced no-shows.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Most businesses see positive ROI within three to six months of implementation. The time saved on administrative tasks alone often justifies the investment, not to mention the revenue gains from improved booking efficiency and customer satisfaction. Free trials and money-back guarantees allow you to test platforms risk-free before committing.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Industry-Specific Solutions</h3>

                  <p className="mb-6 leading-relaxed">
                    While general-purpose booking software works for many businesses, industry-specific solutions offer tailored features that address unique requirements. Healthcare providers need HIPAA compliance and electronic health record integration. Fitness studios require class scheduling and membership management. Salons benefit from inventory tracking for retail products.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Specialized platforms understand industry workflows and terminology, reducing configuration time and improving user adoption. They often include pre-built templates, industry-specific reporting, and integrations with sector-specific tools. However, they may cost more and offer less flexibility than general solutions.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">The Future of Booking Software</h3>

                  <p className="mb-6 leading-relaxed">
                    Looking ahead, booking software will continue evolving with emerging technologies. Voice-activated booking through smart speakers and virtual assistants is gaining traction. Augmented reality features allow customers to preview services or facilities before booking. Blockchain technology promises enhanced security and transparency in payment processing.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Artificial intelligence will become more sophisticated, offering personalized recommendations, dynamic pricing optimization, and predictive customer service. These advancements will further blur the line between booking software and comprehensive business management platforms, creating all-in-one solutions that handle every aspect of small business operations.
                  </p>

                  <h3 className="text-3xl font-semibold mt-12 mb-6">Making Your Decision</h3>

                  <p className="mb-6 leading-relaxed">
                    Selecting the right booking software requires careful evaluation of your business needs, budget constraints, and growth plans. Start by listing your must-have features and nice-to-have capabilities. Research platforms that specialize in your industry or business size. Take advantage of free trials to test usability and feature sets.
                  </p>

                  <p className="mb-6 leading-relaxed">
                    Read user reviews and case studies from businesses similar to yours. Pay attention to customer support quality—responsive, knowledgeable support can make the difference between successful implementation and frustrating struggles. Don't hesitate to request demos and ask detailed questions about features, pricing, and roadmap.
                  </p>

                  <p className="mb-8 leading-relaxed">
                    Remember that the cheapest option isn't always the best value, and the most feature-rich platform may be overkill for your needs. The ideal solution balances functionality, usability, and cost while supporting your business's unique requirements and growth trajectory. With the right booking software in place, you'll be positioned to deliver exceptional customer experiences, operate more efficiently, and scale your business successfully in 2026 and beyond.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </article>

        <section className="py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join thousands of small businesses that have revolutionized their operations with modern booking software. Start your journey today.
              </p>
              <Button 
                size="lg" 
                className="text-lg px-12 py-6 shadow-xl hover:shadow-2xl transition-all group"
                asChild
              >
                <a 
                  href="https://rebrand.ly/booking-software-small-business" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Going with that now
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}